import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  IconButton,
  InputAdornment,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import { Horse } from '@phosphor-icons/react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import {
  SMART_COLORS as COLORS,
  smartCardSx as cardSx,
  smartTableContainerSx,
  smartTableHeadSx,
  smartTableSx,
} from '../../theme/smartStableTheme';

const apiUrl = process.env.REACT_APP_API_URL;

const COPY = {
  ar: {
    title: 'تقرير نقل الخيل',
    subtitle: 'مراجعة أوامر النقل، السائقين، التكلفة، وعدد الخيل في كل رحلة.',
    create: 'إنشاء طلب نقل',
    refresh: 'تحديث',
    search: 'بحث بالسائق أو الوجهة أو الموقع',
    records: 'طلبات النقل',
    horses: 'الخيل المنقولة',
    revenue: 'إجمالي التكلفة',
    avgMargin: 'متوسط الهامش',
    driver: 'السائق',
    phone: 'الهاتف',
    route: 'المسار',
    cost: 'التكلفة',
    margin: 'الهامش',
    date: 'التاريخ',
    count: 'عدد الخيل',
    details: 'التفاصيل',
    noData: 'لا توجد طلبات نقل مطابقة',
    loadError: 'تعذر تحميل بيانات النقل.',
    owner: 'المالك',
  },
  en: {
    title: 'Horse Transport Report',
    subtitle: 'Review transport orders, drivers, cost, margin, and horses per trip.',
    create: 'Create Transport Order',
    refresh: 'Refresh',
    search: 'Search driver, destination, or location',
    records: 'Transport orders',
    horses: 'Transported horses',
    revenue: 'Total cost',
    avgMargin: 'Avg margin',
    driver: 'Driver',
    phone: 'Phone',
    route: 'Route',
    cost: 'Cost',
    margin: 'Margin',
    date: 'Date',
    count: 'Horse count',
    details: 'Details',
    noData: 'No matching transport orders',
    loadError: 'Could not load transport data.',
    owner: 'Owner',
  },
};

const moneyFormatter = new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 });

function formatMoney(value) {
  return `${moneyFormatter.format(Number(value || 0))} SAR`;
}

function formatDate(value) {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleString();
}

function MetricCard({ icon, title, value, helper, color = COLORS.gold, soft = COLORS.goldSoft }) {
  return (
    <Paper elevation={0} sx={{ ...cardSx, p: 2, minHeight: 100 }}>
      <Stack direction="row" spacing={1.5} alignItems="center">
        <Box sx={{ width: 48, height: 48, borderRadius: '50%', bgcolor: soft, color, display: 'grid', placeItems: 'center' }}>
          {icon}
        </Box>
        <Box>
          <Typography sx={{ color, fontWeight: 800 }}>{title}</Typography>
          <Typography variant="h5" sx={{ color: COLORS.text, fontWeight: 900 }}>{value}</Typography>
          {helper && <Typography variant="caption" sx={{ color: COLORS.muted }}>{helper}</Typography>}
        </Box>
      </Stack>
    </Paper>
  );
}

const HorseTransportReportPage = () => {
  const { i18n } = useTranslation();
  const text = COPY[i18n.language === 'ar' ? 'ar' : 'en'];
  const [records, setRecords] = useState([]);
  const [expandedRow, setExpandedRow] = useState(null);
  const [expandedHorses, setExpandedHorses] = useState([]);
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const fetchRecords = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await axios.get(`${apiUrl}/transport`);
      setRecords(res.data || []);
    } catch (err) {
      console.error('Error fetching transport records:', err);
      setError(text.loadError);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRecords();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filteredRecords = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return records;
    return records.filter((record) =>
      [record.driver_name, record.driver_phone, record.from_location, record.to_location, record.transport_id]
        .some((value) => String(value || '').toLowerCase().includes(needle))
    );
  }, [query, records]);

  const summary = useMemo(() => {
    const totalCost = filteredRecords.reduce((sum, record) => sum + Number(record.total_cost || 0), 0);
    const totalMargin = filteredRecords.reduce((sum, record) => sum + Number(record.profit_margin || 0), 0);
    const horseCount = filteredRecords.reduce((sum, record) => sum + Number(record.horse_count || 0), 0);
    return {
      totalCost,
      horseCount,
      avgMargin: filteredRecords.length ? totalMargin / filteredRecords.length : 0,
    };
  }, [filteredRecords]);

  const handleExpand = async (transportId) => {
    if (expandedRow === transportId) {
      setExpandedRow(null);
      setExpandedHorses([]);
      return;
    }

    try {
      const res = await axios.get(`${apiUrl}/transport/${transportId}/horses`);
      setExpandedHorses(res.data || []);
      setExpandedRow(transportId);
    } catch (err) {
      console.error('Error fetching horse details:', err);
      setExpandedHorses([]);
      setExpandedRow(transportId);
    }
  };

  return (
    <Box
      className="smart-page-container"
      sx={{
        minHeight: '100vh',
        bgcolor: COLORS.page,
        width: '100%',
        maxWidth: 'var(--app-page-max)',
        mx: 'auto',
        overflowX: 'hidden',
      }}
    >
      <Paper elevation={0} sx={{ ...cardSx, p: { xs: 2, md: 3 }, mb: 3 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} justifyContent="space-between" alignItems={{ xs: 'stretch', md: 'center' }}>
          <Box>
            <Typography variant="h4" sx={{ color: COLORS.text, fontWeight: 900 }}>{text.title}</Typography>
            <Typography sx={{ color: COLORS.muted, mt: 0.5 }}>{text.subtitle}</Typography>
          </Box>
          <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
            <Button variant="outlined" startIcon={<RefreshIcon />} onClick={fetchRecords} sx={{ borderColor: COLORS.line, color: COLORS.text }}>
              {text.refresh}
            </Button>
            <Button variant="contained" startIcon={<AddIcon />} href="/HorseTransportForm" sx={{ bgcolor: COLORS.gold, '&:hover': { bgcolor: COLORS.goldHover } }}>
              {text.create}
            </Button>
          </Stack>
        </Stack>
      </Paper>

      {error && <Alert severity="warning" sx={{ mb: 2 }}>{error}</Alert>}

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(4, minmax(0, 1fr))' }, gap: 2, mb: 3, width: '100%' }}>
        <MetricCard icon={<LocalShippingIcon />} title={text.records} value={filteredRecords.length} />
        <MetricCard icon={<Horse size={26} weight="fill" />} title={text.horses} value={summary.horseCount} color={COLORS.blue} soft={COLORS.blueSoft} />
        <MetricCard icon={<LocalShippingIcon />} title={text.revenue} value={formatMoney(summary.totalCost)} color={COLORS.green} soft={COLORS.greenSoft} />
        <MetricCard icon={<LocalShippingIcon />} title={text.avgMargin} value={formatMoney(summary.avgMargin)} color={COLORS.rose} soft={COLORS.roseSoft} />
      </Box>

      <Paper elevation={0} sx={{ ...cardSx, overflow: 'hidden' }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="space-between" sx={{ p: 2 }}>
          <TextField
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={text.search}
            size="small"
            sx={{ width: { xs: '100%', sm: 420 } }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
          <Chip label={`${filteredRecords.length} ${text.records}`} sx={{ bgcolor: COLORS.goldSoft, color: COLORS.text, fontWeight: 800 }} />
        </Stack>

        {loading ? (
          <Stack alignItems="center" sx={{ py: 6 }}><CircularProgress /></Stack>
        ) : (
          <TableContainer sx={{ ...smartTableContainerSx, width: '100%' }}>
            <Table sx={{ ...smartTableSx, minWidth: 980 }}>
              <TableHead sx={smartTableHeadSx}>
                <TableRow>
                  <TableCell />
                  <TableCell>ID</TableCell>
                  <TableCell>{text.driver}</TableCell>
                  <TableCell>{text.phone}</TableCell>
                  <TableCell>{text.route}</TableCell>
                  <TableCell>{text.cost}</TableCell>
                  <TableCell>{text.margin}</TableCell>
                  <TableCell>{text.date}</TableCell>
                  <TableCell>{text.count}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredRecords.map((record) => (
                  <React.Fragment key={record.transport_id}>
                    <TableRow hover sx={{ '& .MuiTableCell-root': { borderBottom: `1px solid ${COLORS.line}` } }}>
                      <TableCell>
                        <Tooltip title={text.details}>
                          <IconButton size="small" onClick={() => handleExpand(record.transport_id)} sx={{ color: COLORS.gold }}>
                            {expandedRow === record.transport_id ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                      <TableCell>{record.transport_id}</TableCell>
                      <TableCell>
                        <Typography sx={{ fontWeight: 900, color: COLORS.text }}>{record.driver_name || '-'}</Typography>
                      </TableCell>
                      <TableCell>{record.driver_phone || '-'}</TableCell>
                      <TableCell>
                        <Typography sx={{ fontWeight: 800 }}>{record.from_location || '-'}</Typography>
                        <Typography variant="caption" sx={{ color: COLORS.muted }}>→ {record.to_location || '-'}</Typography>
                      </TableCell>
                      <TableCell>{formatMoney(record.total_cost)}</TableCell>
                      <TableCell>{formatMoney(record.profit_margin)}</TableCell>
                      <TableCell>{formatDate(record.transport_date)}</TableCell>
                      <TableCell>
                        <Chip size="small" label={record.horse_count || 0} sx={{ bgcolor: COLORS.blueSoft, color: COLORS.text, fontWeight: 800 }} />
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell colSpan={9} sx={{ p: 0, borderBottom: expandedRow === record.transport_id ? `1px solid ${COLORS.line}` : 0 }}>
                        <Collapse in={expandedRow === record.transport_id} timeout="auto" unmountOnExit>
                          <Box sx={{ p: 2, bgcolor: COLORS.cardSoft }}>
                            <Typography sx={{ fontWeight: 900, color: COLORS.text, mb: 1 }}>{text.horses}</Typography>
                            <Stack direction="row" flexWrap="wrap" gap={1}>
                              {expandedHorses.map((horse) => (
                                <Chip
                                  key={`${record.transport_id}-${horse.horse_id}`}
                                  icon={<Horse size={18} weight="fill" />}
                                  label={`${horse.horse_name || '-'} · ${text.owner}: ${horse.owner_name || '-'}`}
                                  sx={{ bgcolor: '#fff', border: `1px solid ${COLORS.line}` }}
                                />
                              ))}
                              {!expandedHorses.length && <Typography sx={{ color: COLORS.muted }}>{text.noData}</Typography>}
                            </Stack>
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                ))}
                {!filteredRecords.length && (
                  <TableRow>
                    <TableCell colSpan={9}>
                      <Typography sx={{ color: COLORS.muted, py: 4, textAlign: 'center' }}>{text.noData}</Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>
    </Box>
  );
};

export default HorseTransportReportPage;
